import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import Joyride, { STATUS, EVENTS, ACTIONS, type CallBackProps } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { TOUR_STEPS } from './tutorialSteps';

/* ── Context ── */

interface TutorialCtx {
  startTour: () => void;
  isActive: boolean;
}

const TutorialContext = createContext<TutorialCtx>({
  startTour: () => {},
  isActive: false,
});

export const useTutorial = () => useContext(TutorialContext);

const STORAGE_KEY = 'kpi-tour-completed';

/* ── Provider ── */

export function TutorialProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [run, setRun] = useState(() => !localStorage.getItem(STORAGE_KEY));
  const [stepIndex, setStepIndex] = useState(0);

  const startTour = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setStepIndex(0);
    // Navigate to the first step's page
    const firstPage = (TOUR_STEPS[0] as any).data?.page || '/dashboard';
    if (location.pathname !== firstPage) {
      navigate(firstPage);
    }
    // Small delay to let navigation settle
    setTimeout(() => setRun(true), 150);
  }, [navigate, location.pathname]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action, index, type } = data;

      // Tour finished or skipped
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        setRun(false);
        setStepIndex(0);
        localStorage.setItem(STORAGE_KEY, 'true');
        return;
      }

      // User closed a tooltip
      if (action === ACTIONS.CLOSE) {
        setRun(false);
        setStepIndex(0);
        localStorage.setItem(STORAGE_KEY, 'true');
        return;
      }

      // After a step completes (Next / Back)
      if (type === EVENTS.STEP_AFTER) {
        const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;

        if (nextIndex < 0 || nextIndex >= TOUR_STEPS.length) {
          setRun(false);
          localStorage.setItem(STORAGE_KEY, 'true');
          return;
        }

        const nextStep = TOUR_STEPS[nextIndex] as any;
        const nextPage: string = nextStep.data?.page || '/dashboard';

        if (location.pathname !== nextPage) {
          // Pause → navigate → wait for DOM → resume
          setRun(false);
          navigate(nextPage);
          setTimeout(() => {
            setStepIndex(nextIndex);
            setRun(true);
          }, 400);
        } else {
          setStepIndex(nextIndex);
        }
      }
    },
    [location.pathname, navigate],
  );

  return (
    <TutorialContext.Provider value={{ startTour, isActive: run }}>
      {children}
      <Joyride
        steps={TOUR_STEPS}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        callback={handleCallback}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          skip: 'Skip tour',
        }}
        styles={{
          options: {
            primaryColor: '#0c6b62',
            zIndex: 10000,
            arrowColor: '#fff',
            backgroundColor: '#fff',
            textColor: '#0e2c32',
          },
          tooltipTitle: {
            fontSize: '1rem',
            fontWeight: 700,
            fontFamily: '"Spline Sans", "Segoe UI", system-ui, sans-serif',
          },
          tooltipContent: {
            fontSize: '0.88rem',
            lineHeight: 1.55,
            fontFamily: '"Spline Sans", "Segoe UI", system-ui, sans-serif',
          },
          buttonNext: {
            borderRadius: 999,
            fontWeight: 600,
            fontSize: '0.82rem',
            padding: '6px 18px',
          },
          buttonBack: {
            color: '#5a6a73',
            fontSize: '0.82rem',
          },
          buttonSkip: {
            color: '#5a6a73',
            fontSize: '0.78rem',
          },
          spotlight: {
            borderRadius: 12,
          },
        }}
      />
    </TutorialContext.Provider>
  );
}
